import { useState, useMemo, useCallback } from 'react';

import {
  Button,
  Text,
  useToast,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  HStack,
  VStack,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';

import { useQuery } from '@tanstack/react-query';

import Card from 'components/card/Card';
import { useAppSelector, useAppDispatch } from 'hooks';
import {
  setHasUnsavedChanges,
  setWipStatus,
  useLegacyAutoFormLocked,
} from 'reducers/legacyAutoFormReducer';
import {
  apiSaveLegacyAutoForm,
  apiSubmitLegacyAutoForm,
  apiGetApplicationPayments,
} from 'api';
import { WIP_STATUS_BINDER_PENDING_UPLOAD } from 'variables/constants';

interface SubmitSectionProps {
  policyUuid: string;
  canEditPremium?: boolean;
}

const SubmitSection = ({
  policyUuid,
  canEditPremium = false,
}: SubmitSectionProps) => {
  const dispatch = useAppDispatch();
  const isLocked = useLegacyAutoFormLocked();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const bgColor = useColorModeValue('white', 'gray.800');

  const formData = useAppSelector((state) => state.legacyAutoForm.data);
  const hasUnsavedChanges = useAppSelector(
    (state) => state.legacyAutoForm.hasUnsavedChanges,
  );
  const premiumConfirmed = useAppSelector(
    (state) => state.legacyAutoForm.premiumConfirmed,
  );

  const { data: paymentsData } = useQuery({
    queryKey: ['application-payments', policyUuid],
    queryFn: () => apiGetApplicationPayments(policyUuid),
    refetchInterval: 5000,
    enabled: !!policyUuid && !isLocked,
  });

  const requiresPaymentInfo =
    formData.billing.payment_method === 'eft' ||
    formData.billing.payment_method === 'credit_card';

  const hasPaymentInfo = useMemo(() => {
    if (!requiresPaymentInfo) return true;
    if (!paymentsData || paymentsData.length === 0) return false;

    return paymentsData.some(
      (payment: { type_name: string; section_name: string }) =>
        payment.type_name === formData.billing.payment_method &&
        payment.section_name === 'payment_method',
    );
  }, [requiresPaymentInfo, paymentsData, formData.billing.payment_method]);

  const areRequiredFieldsFilled = useMemo(() => {
    const {
      client_information,
      binder_information,
      billing,
      additional_info,
      telematics,
    } = formData;

    const baseFieldsFilled = !!(
      client_information.client_name &&
      client_information.client_code &&
      binder_information.binder_number &&
      binder_information.effective_date &&
      binder_information.insurance_company &&
      parseFloat(binder_information.premium) > 0 &&
      additional_info.all_documents_attached &&
      billing.payment_date_requested &&
      billing.payment_type &&
      billing.payment_method
    );

    let isValid = baseFieldsFilled;

    if (telematics?.has_telematics) {
      isValid =
        isValid && !!telematics.phone?.trim() && !!telematics.email?.trim();
    }

    if (billing.down_payment) {
      isValid =
        isValid &&
        billing.down_payment_amount > 0 &&
        !!billing.down_payment_type;
    }

    if (billing.payment_method === 'agency_bill') {
      const agencyBilling = billing.agency_billing;
      isValid =
        isValid &&
        !!agencyBilling.financing_type &&
        !!agencyBilling.province_code &&
        !!agencyBilling.total_premium &&
        !!agencyBilling.company_fee &&
        !!agencyBilling.broker_fee &&
        !!agencyBilling.tax;

      if (agencyBilling.financing_type === 'premium_financing') {
        isValid = isValid && agencyBilling.is_signed_contract_attached;
      }
    }

    return isValid;
  }, [formData]);

  // Check if premium confirmation is required and completed
  const isPremiumConfirmed = useMemo(() => {
    const totalPremium = parseFloat(
      formData.billing.agency_billing.total_premium || '0',
    );
    return totalPremium <= 10000 || premiumConfirmed;
  }, [formData.billing.agency_billing.total_premium, premiumConfirmed]);

  const canSubmit = useMemo(() => {
    return areRequiredFieldsFilled && hasPaymentInfo && isPremiumConfirmed;
  }, [areRequiredFieldsFilled, hasPaymentInfo, isPremiumConfirmed]);

  const getMissingRequirementsMessage = useCallback(() => {
    const missing: string[] = [];
    const {
      client_information,
      binder_information,
      billing,
      additional_info,
      telematics,
    } = formData;

    if (!isPremiumConfirmed) {
      missing.push(
        'Please confirm the premium amount is correct (exceeds $10,000)',
      );
    }

    if (!client_information.client_name) missing.push('Client Name');
    if (!client_information.client_code) missing.push('Client Code');
    if (!binder_information.binder_number) missing.push('Binder Number');
    if (!binder_information.effective_date) missing.push('Effective Date');
    if (!binder_information.insurance_company)
      missing.push('Insurance Company');
    if (!(parseFloat(binder_information.premium) > 0)) missing.push('Premium');
    if (!additional_info.all_documents_attached)
      missing.push('Document Compliance Confirmation');
    if (!billing.payment_date_requested) missing.push('Payment Date Requested');
    if (!billing.payment_type) missing.push('Payment Type');
    if (!billing.payment_method) missing.push('Payment Method');

    if (billing.down_payment) {
      if (!billing.down_payment_amount || billing.down_payment_amount <= 0) {
        missing.push('Down Payment Amount');
      }
      if (!billing.down_payment_type) {
        missing.push('Down Payment Type');
      }
    }

    if (billing.payment_method === 'agency_bill') {
      const agencyBilling = billing.agency_billing;
      if (!agencyBilling.financing_type) missing.push('Financing Type');
      if (
        agencyBilling.financing_type === 'premium_financing' &&
        !agencyBilling.is_signed_contract_attached
      ) {
        missing.push('Signed Contract Attached');
      }
      if (!agencyBilling.province_code) missing.push('Province');
      if (!agencyBilling.total_premium) missing.push('Total Premium');
      if (!agencyBilling.company_fee) missing.push('Company Fee');
      if (!agencyBilling.broker_fee) missing.push('Broker Fee');
      if (!agencyBilling.tax) missing.push('Tax');
    }

    if (requiresPaymentInfo && !hasPaymentInfo) {
      const methodLabel =
        billing.payment_method === 'eft' ? 'EFT' : 'Credit Card';
      missing.push(`${methodLabel} Payment Information`);
    }

    if (telematics?.has_telematics) {
      if (!telematics.phone?.trim())
        missing.push('Telematics Cell Phone Number');
      if (!telematics.email?.trim()) missing.push('Telematics Email Address');
    }

    return missing;
  }, [formData, requiresPaymentInfo, hasPaymentInfo, isPremiumConfirmed]);

  const buildSaveData = () => ({
    client_information: formData.client_information,
    binder_information: {
      ...formData.binder_information,
      premium: parseFloat(formData.binder_information.premium) || 0,
    },
    billing: formData.billing,
    telematics: formData.telematics,
    additional_info: formData.additional_info,
    premium_confirmed: premiumConfirmed,
  });

  const handleSave = async () => {
    setIsSaving(true);

    try {
      await apiSaveLegacyAutoForm(policyUuid, buildSaveData());
      dispatch(setHasUnsavedChanges(false));

      toast({
        title: 'Saved',
        description: 'Form data saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error?.response?.data?.error ||
          error?.message ||
          'Failed to save form. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      await apiSaveLegacyAutoForm(policyUuid, buildSaveData());
      const response = await apiSubmitLegacyAutoForm(policyUuid);

      dispatch(setHasUnsavedChanges(false));
      dispatch(setWipStatus(WIP_STATUS_BINDER_PENDING_UPLOAD));

      toast({
        title: 'Success',
        description:
          response.detail || 'Legacy auto form submitted successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to submit form. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const missingRequirements = getMissingRequirementsMessage();

  return (
    <>
      {!isLocked && (
        <Card mb="20px">
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="bold">
              Form Submission
            </Text>

            {!canSubmit && missingRequirements.length > 0 && (
              <Alert status="warning">
                <AlertIcon />
                <AlertDescription>
                  <Text fontWeight="bold" mb={1}>
                    Please complete the following before submitting:
                  </Text>
                  <ul style={{ marginLeft: '20px' }}>
                    {missingRequirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {canSubmit && (
              <Alert status="success">
                <AlertIcon />
                <AlertDescription>
                  All required fields are complete. You can now submit the form.
                </AlertDescription>
              </Alert>
            )}

            <Flex justify="flex-end">
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting || hasUnsavedChanges}
                isLoading={isSubmitting}
                loadingText="Submitting..."
                colorScheme="brand"
                variant="solid"
                size="lg"
              >
                Submit
              </Button>
            </Flex>
          </VStack>
        </Card>
      )}

      {hasUnsavedChanges && (!isLocked || canEditPremium) && (
        <Box
          position="fixed"
          bottom="20px"
          right="20px"
          zIndex={1000}
          maxW="500px"
        >
          <Card bg={bgColor} p={4} shadow="lg" borderWidth="1px">
            <HStack spacing={6}>
              <Text>You have unsaved changes</Text>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                isLoading={isSaving}
                loadingText="Saving..."
                colorScheme="yellow"
                variant="solid"
              >
                Save Changes
              </Button>
            </HStack>
          </Card>
        </Box>
      )}
    </>
  );
};

export default SubmitSection;
